import { getAuthToken } from '../auth';
import { apiPost } from '../../utils/api';
import { config } from '../../config/app-config';
import { createModal } from './ui-elements';
import { updatePendingChanges } from './content-store';

export function openYoutubeEditor(element, options = {}) {
  const pageTitle = element.getAttribute('data-page') || 'Home';
  const tagId = element.getAttribute('data-content');
  const blockName = element.getAttribute('data-block-name') || findBlockName(element);
  const dataAttribute = element.getAttribute('data-attribute') || 'video';

  // Get current video ID if exists
  let currentVideoId = '';
  const iframe = element.querySelector('iframe');
  if (iframe && iframe.src) {
    const match = iframe.src.match(/\/embed\/([^?&]+)/);
    if (match && match[1]) {
      currentVideoId = match[1];
    }
  }

  const modalContent = `
    <h3>Edit YouTube Video</h3>
    <p>Enter YouTube video URL or ID:</p>
    <input type="text" id="youtube-input" value="${currentVideoId ? `https://www.youtube.com/watch?v=${currentVideoId}` : ''}" placeholder="https://www.youtube.com/watch?v=VIDEO_ID">
    ${blockName ? `<div class="block-info">Block: ${blockName}</div>` : ''}
  `;

  const modal = createModal(modalContent);

  const youtubeInput = document.getElementById('youtube-input');
  youtubeInput.focus();
  youtubeInput.select();

  modal.on('save', () => {
    const youtubeUrl = youtubeInput.value.trim();
    if (youtubeUrl) {
      saveYoutubeChange(element, {
        pageTitle,
        tagId,
        youtubeUrl,
        blockName,
        dataAttribute
      });
    }
    modal.close();
  });
}

export function saveYoutubeChange(element, data) {
  // Extract video ID from URL
  let videoId = extractYoutubeId(data.youtubeUrl);

  if (!videoId) {
    console.error('Invalid YouTube URL or video ID:', data.youtubeUrl);
    return;
  }

  // Create object without direct DOM element reference
  const changeData = {
    pageTitle: data.pageTitle,
    tagId: data.tagId,
    youtubeUrl: data.youtubeUrl,
    videoId: videoId,
    blockName: data.blockName,
    dataAttribute: data.dataAttribute,
    // Save URL in media_path instead of content
    media_path: data.youtubeUrl,
    content: null
  };

  updatePendingChanges('youtube', data.tagId, changeData);

  // Apply changes to DOM (preview)
  updateYoutubePlayer(element, videoId);
  element.classList.add('content-modified');

  const publishBtn = document.getElementById(config.editor.selectors.publishButton);
  if (publishBtn) {
    publishBtn.style.display = 'block';
  }
}

export async function publishYoutubeChange(tagId, change) {
  const token = getAuthToken();
  if (!token) {
    return Promise.reject(new Error('Authentication required'));
  }

  const formData = new FormData();
  formData.append('youtube_url', change.youtubeUrl);
  formData.append('page_title', change.pageTitle);
  formData.append('tag_id', change.tagId);
  formData.append('tag', change.tagId);
  formData.append('order', 1);
  formData.append('data_attribute', change.dataAttribute || 'video');

  if (change.blockName) {
    formData.append('block_name', change.blockName);
  }

  try {
    const result = await apiPost(config.api.paths.contentVideo, formData);

    // Find element in DOM by ID
    const elementInDOM = document.querySelector(`[data-content="${tagId}"]`);
    if (elementInDOM) {
      elementInDOM.classList.remove('content-modified');
    } else {
      console.warn(`Element with tagId ${tagId} not found in DOM`);
    }

    return tagId;
  } catch (error) {
    console.error(`Failed to publish YouTube video change for ${tagId}:`, error);
    throw error;
  }
}

function extractYoutubeId(url) {
  if (!url) return null;

  // If it's just a video ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  // YouTube URL formats:
  // - https://www.youtube.com/watch?v=VIDEO_ID
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/embed/VIDEO_ID

  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return (match && match[2].length === 11) ? match[2] : null;
}

function updateYoutubePlayer(element, videoId) {
  // Create YouTube iframe
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.youtube.com/embed/${videoId}?rel=0`;
  iframe.width = '100%';
  iframe.height = '100%';
  iframe.frameBorder = '0';
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.style.borderRadius = 'inherit';

  // Clear element content and add iframe
  element.innerHTML = '';
  element.appendChild(iframe);
}

function findBlockName(element) {
  let parent = element.parentElement;
  while (parent) {
    if (parent.hasAttribute('data-block-name')) {
      return parent.getAttribute('data-block-name');
    }
    parent = parent.parentElement;
  }
  return '';
}